import React, { useState } from 'react';
import { Plus, X, Save, Code, Type, Table, Check } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';

interface Zone {
  id: string;
  name: string;
  type: 'text' | 'table' | 'checkbox' | 'date';
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  rules: ExtractionRule[];
  required: boolean;
}

interface ExtractionRule {
  id: string;
  type: 'regex' | 'keyword' | 'position' | 'table';
  value: string;
  config: Record<string, any>;
}

interface RuleBuilderProps {
  zone: Zone;
  onUpdateZone: (zone: Zone) => void;
}

const RULE_TYPES = {
  regex: {
    icon: Code,
    label: 'Regular Expression',
    description: 'Extract text matching a pattern',
    defaultConfig: {}
  },
  keyword: {
    icon: Type,
    label: 'Keyword',
    description: 'Extract text based on keywords',
    defaultConfig: {
      caseSensitive: false,
      wholeWord: true
    }
  },
  position: {
    icon: Check,
    label: 'Position',
    description: 'Extract text based on position',
    defaultConfig: {
      offset: 0,
      length: 0
    }
  },
  table: {
    icon: Table,
    label: 'Table',
    description: 'Extract data in tabular format',
    defaultConfig: {
      hasHeader: true,
      columns: []
    }
  }
};

const RuleBuilder: React.FC<RuleBuilderProps> = ({ zone, onUpdateZone }) => {
  const [newRuleType, setNewRuleType] = useState<keyof typeof RULE_TYPES>('regex');
  const [showNewRule, setShowNewRule] = useState(false);

  const handleAddRule = () => {
    const newRule: ExtractionRule = {
      id: crypto.randomUUID(),
      type: newRuleType,
      value: '',
      config: RULE_TYPES[newRuleType].defaultConfig
    };

    onUpdateZone({
      ...zone,
      rules: [...zone.rules, newRule]
    });
    setShowNewRule(false);
  };

  const handleUpdateRule = (ruleId: string, updates: Partial<ExtractionRule>) => {
    onUpdateZone({
      ...zone,
      rules: zone.rules.map(rule =>
        rule.id === ruleId ? { ...rule, ...updates } : rule
      )
    });
  };

  const handleDeleteRule = (ruleId: string) => {
    onUpdateZone({
      ...zone,
      rules: zone.rules.filter(rule => rule.id !== ruleId)
    });
  };

  const renderRuleConfig = (rule: ExtractionRule) => {
    switch (rule.type) {
      case 'regex':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-purple-400 mb-1">Pattern</label>
              <input
                type="text"
                value={rule.value}
                onChange={(e) => handleUpdateRule(rule.id, { value: e.target.value })}
                placeholder="Enter regex pattern"
                className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
              />
            </div>
            <div>
              <label className="block text-sm text-purple-400 mb-1">Test Text</label>
              <input
                type="text"
                placeholder="Test your regex pattern"
                className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
              />
            </div>
          </div>
        );

      case 'keyword':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-purple-400 mb-1">Keywords</label>
              <input
                type="text"
                value={rule.value}
                onChange={(e) => handleUpdateRule(rule.id, { value: e.target.value })}
                placeholder="Enter keywords (comma separated)"
                className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
              />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rule.config.caseSensitive}
                  onChange={(e) => handleUpdateRule(rule.id, {
                    config: { ...rule.config, caseSensitive: e.target.checked }
                  })}
                  className="rounded border-purple-500/20"
                />
                <span className="text-sm text-purple-300">Case Sensitive</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rule.config.wholeWord}
                  onChange={(e) => handleUpdateRule(rule.id, {
                    config: { ...rule.config, wholeWord: e.target.checked }
                  })}
                  className="rounded border-purple-500/20"
                />
                <span className="text-sm text-purple-300">Whole Word</span>
              </label>
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={rule.config.hasHeader}
                onChange={(e) => handleUpdateRule(rule.id, {
                  config: { ...rule.config, hasHeader: e.target.checked }
                })}
                className="rounded border-purple-500/20"
              />
              <span className="text-sm text-purple-300">Has Header Row</span>
            </label>
            <div>
              <label className="block text-sm text-purple-400 mb-1">Column Names</label>
              <input
                type="text"
                value={rule.config.columns.join(', ')}
                onChange={(e) => handleUpdateRule(rule.id, {
                  config: { ...rule.config, columns: e.target.value.split(',').map(s => s.trim()) }
                })}
                placeholder="Enter column names (comma separated)"
                className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Zone Properties */}
      <Card className="bg-purple-500/5">
        <CardContent className="p-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-purple-400 mb-1">Zone Name</label>
              <input
                type="text"
                value={zone.name}
                onChange={(e) => onUpdateZone({ ...zone, name: e.target.value })}
                className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
              />
            </div>
            <div>
              <label className="block text-sm text-purple-400 mb-1">Data Type</label>
              <select
                value={zone.type}
                onChange={(e) => onUpdateZone({ ...zone, type: e.target.value as Zone['type'] })}
                className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
              >
                <option value="text">Text</option>
                <option value="table">Table</option>
                <option value="checkbox">Checkbox</option>
                <option value="date">Date</option>
              </select>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={zone.required}
                onChange={(e) => onUpdateZone({ ...zone, required: e.target.checked })}
                className="rounded border-purple-500/20"
              />
              <span className="text-sm text-purple-300">Required Field</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Extraction Rules */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-purple-400">Extraction Rules</h3>
          <button
            onClick={() => setShowNewRule(true)}
            className="flex items-center gap-1 text-sm text-purple-300 hover:text-purple-200"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>

        {showNewRule && (
          <Card className="bg-purple-500/5 mb-4">
            <CardContent className="p-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Rule Type</label>
                  <select
                    value={newRuleType}
                    onChange={(e) => setNewRuleType(e.target.value as keyof typeof RULE_TYPES)}
                    className="w-full bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2 text-purple-100"
                  >
                    {Object.entries(RULE_TYPES).map(([type, { label }]) => (
                      <option key={type} value={type}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => setShowNewRule(false)}
                    className="px-3 py-1 text-sm text-purple-300 hover:text-purple-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddRule}
                    className="flex items-center gap-2 px-3 py-1 bg-purple-500 text-white rounded-lg text-sm"
                  >
                    <Save className="w-4 h-4" />
                    Add Rule
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {zone.rules.map((rule) => {
            const RuleIcon = RULE_TYPES[rule.type].icon;
            return (
              <Card key={rule.id} className="bg-purple-500/5">
                <CardHeader className="p-4 pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RuleIcon className="w-4 h-4 text-purple-400" />
                      <CardTitle className="text-sm font-medium text-purple-100">
                        {RULE_TYPES[rule.type].label}
                      </CardTitle>
                    </div>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="text-purple-400 hover:text-purple-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-2">
                  {renderRuleConfig(rule)}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RuleBuilder;